"use client";

import { cn, onBtnClick } from "@garther/ui/libs";
import { useEffect } from "react";
import { createPortal } from "react-dom";

interface PopupProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
}

export const Popup = ({
  isOpen,
  onClose,
  children,
  className,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true
}: PopupProps) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && closeOnEscape) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, closeOnEscape]);

  if (!isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && closeOnBackdropClick) {
      onClose();
    }
  };
  const root =
    document.querySelector("#root") ??
    document.querySelector("#app") ??
    document.querySelector("#__next") ??
    document.body;
  if (typeof root === "undefined" || root === null) {
    console.log("Popup is not rendering!");
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-background-highlight backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className={cn(
          "relative bg-background-primary rounded-sm shadow-xl max-w-md w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto",
          "border border-border-dark",
          "transform transition-all duration-300 ease-out",
          "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-4",
          className
        )}
      >
        {showCloseButton && (
          <button
            onClick={onBtnClick(onClose)}
            className="absolute top-4 right-4 p-1 rounded-full hover:bg-gray-100 transition-colors z-10"
            aria-label="Close"
          >
            x
          </button>
        )}
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </div>,
    root
  );
};
